import type { Cents } from './money.ts';

// Precios, costos, márgenes y redondeos. Todo en centavos enteros.
// Las alícuotas de IVA se guardan en puntos básicos: 2100 = 21 %.

export const TAX_RATES = [
  { bps: 2100, label: '21 %' },
  { bps: 1050, label: '10,5 %' },
  { bps: 2700, label: '27 %' },
  { bps: 0, label: 'Exento (0 %)' },
] as const;

export const DEFAULT_TAX_BPS = 2100;

export function isValidTaxBps(bps: number): boolean {
  return TAX_RATES.some((t) => t.bps === bps);
}

export type RoundingMode = 'up' | 'nearest';

/**
 * Redondea un precio a un múltiplo de `stepCents`. Los comercios suelen redondear
 * "para arriba" a $10, $50 o $100 después de un aumento.
 */
export function roundPrice(cents: Cents, stepCents: number, mode: RoundingMode = 'up'): Cents {
  if (stepCents <= 1) return Math.round(cents);
  const ratio = cents / stepCents;
  const rounded = mode === 'up' ? Math.ceil(ratio - 1e-9) : Math.round(ratio);
  return rounded * stepCents;
}

/** Aumento (o rebaja, con % negativo) masivo con redondeo opcional. Nunca devuelve negativo. */
export function increasePrice(cents: Cents, percent: number, stepCents = 1, mode: RoundingMode = 'up'): Cents {
  const raw = Math.round(cents * (1 + percent / 100));
  return Math.max(0, roundPrice(raw, stepCents, mode));
}

/** Precio a partir del costo y un recargo ("remarcar un 40 %"). */
export function priceFromMarkup(cost: Cents, markupPct: number): Cents {
  return Math.round(cost * (1 + markupPct / 100));
}

/** Recargo sobre el costo, en %: (precio − costo) / costo. null si no hay costo. */
export function markupPct(cost: Cents | null | undefined, price: Cents): number | null {
  if (!cost || cost <= 0) return null;
  return ((price - cost) / cost) * 100;
}

/** Margen sobre el precio de venta, en %: (precio − costo) / precio. */
export function marginPct(cost: Cents | null | undefined, price: Cents): number | null {
  if (cost == null || price <= 0) return null;
  return ((price - cost) / price) * 100;
}

export function formatPct(value: number | null, decimals = 1): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${new Intl.NumberFormat('es-AR', { maximumFractionDigits: decimals }).format(value)} %`;
}
